import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBooking } from '../context/BookingContext';
import { CalendarIcon, CreditCardIcon, CheckIcon, UserIcon, MailIcon, PhoneIcon } from 'lucide-react';
const BookingPage = () => {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const {
    bookingDetails
  } = useBooking();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    cardNumber: '',
    cardExpiry: '',
    cardCvc: '',
    nameOnCard: '',
    agreeToTerms: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);
  // If no booking details, redirect to property page
  if (!bookingDetails || bookingDetails.propertyId !== parseInt(id)) {
    navigate(`/property/${id}`);
    return null;
  }
  const handleChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  const handleSubmit = e => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setBookingComplete(true);
      // After 3 seconds, redirect to dashboard
      setTimeout(() => {
        navigate('/dashboard');
      }, 3000);
    }, 2000);
  };
  const formatDate = date => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  return <div className="bg-[#F8F5F0] min-h-screen w-full py-8" data-id="element-819">
      <div className="container mx-auto px-4" data-id="element-820">
        <div className="max-w-4xl mx-auto" data-id="element-821">
          {bookingComplete ? <div className="bg-white rounded-lg shadow-md p-8 text-center" data-id="element-822">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-823">
                <CheckIcon className="h-8 w-8 text-green-600" data-id="element-824" />
              </div>
              <h2 className="text-2xl font-bold text-[#333333] mb-2" data-id="element-825">
                Booking Confirmed!
              </h2>
              <p className="text-gray-600 mb-6" data-id="element-826">
                Thank you for your booking. We've sent the details to your
                email.
              </p>
              <p className="text-gray-600" data-id="element-827">Redirecting to your dashboard...</p>
            </div> : <>
              <h1 className="text-2xl md:text-3xl font-bold text-[#333333] mb-6" data-id="element-828">
                Complete your booking
              </h1>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-829">
                {/* Booking Form */}
                <div className="md:col-span-2" data-id="element-830">
                  <div className="bg-white rounded-lg shadow-md p-6 mb-6" data-id="element-831">
                    <h2 className="text-xl font-semibold mb-4" data-id="element-832">
                      Your Information
                    </h2>
                    <form onSubmit={handleSubmit} data-id="element-833">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6" data-id="element-834">
                        <div data-id="element-835">
                          <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-836">
                            First Name
                          </label>
                          <div className="relative" data-id="element-837">
                            <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-838" />
                            <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} required className="pl-10 w-full p-3 border border-gray-300 rounded-md focus:ring-[#D4A017] focus:border-[#D4A017] outline-none" placeholder="John" data-id="element-839" />
                          </div>
                        </div>
                        <div data-id="element-840">
                          <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-841">
                            Last Name
                          </label>
                          <div className="relative" data-id="element-842">
                            <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-843" />
                            <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} required className="pl-10 w-full p-3 border border-gray-300 rounded-md focus:ring-[#D4A017] focus:border-[#D4A017] outline-none" placeholder="Doe" data-id="element-844" />
                          </div>
                        </div>
                      </div>
                      <div className="mb-6" data-id="element-845">
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-846">
                          Email
                        </label>
                        <div className="relative" data-id="element-847">
                          <MailIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-848" />
                          <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required className="pl-10 w-full p-3 border border-gray-300 rounded-md focus:ring-[#D4A017] focus:border-[#D4A017] outline-none" placeholder="john.doe@example.com" data-id="element-849" />
                        </div>
                      </div>
                      <div className="mb-6" data-id="element-850">
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-851">
                          Phone Number
                        </label>
                        <div className="relative" data-id="element-852">
                          <PhoneIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-853" />
                          <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} required className="pl-10 w-full p-3 border border-gray-300 rounded-md focus:ring-[#D4A017] focus:border-[#D4A017] outline-none" placeholder="+1 (555) 123-4567" data-id="element-854" />
                        </div>
                      </div>
                      <h2 className="text-xl font-semibold mb-4 mt-8" data-id="element-855">
                        Payment Information
                      </h2>
                      <div className="mb-6" data-id="element-856">
                        <label htmlFor="cardNumber" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-857">
                          Card Number
                        </label>
                        <div className="relative" data-id="element-858">
                          <CreditCardIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-859" />
                          <input type="text" id="cardNumber" name="cardNumber" value={formData.cardNumber} onChange={handleChange} required className="pl-10 w-full p-3 border border-gray-300 rounded-md focus:ring-[#D4A017] focus:border-[#D4A017] outline-none" placeholder="1234 5678 9012 3456" data-id="element-860" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-861">
                        <div data-id="element-862">
                          <label htmlFor="cardExpiry" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-863">
                            Expiry Date
                          </label>
                          <div className="relative" data-id="element-864">
                            <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-865" />
                            <input type="text" id="cardExpiry" name="cardExpiry" value={formData.cardExpiry} onChange={handleChange} required className="pl-10 w-full p-3 border border-gray-300 rounded-md focus:ring-[#D4A017] focus:border-[#D4A017] outline-none" placeholder="MM/YY" data-id="element-866" />
                          </div>
                        </div>
                        <div data-id="element-867">
                          <label htmlFor="cardCvc" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-868">
                            CVC
                          </label>
                          <input type="text" id="cardCvc" name="cardCvc" value={formData.cardCvc} onChange={handleChange} required className="w-full p-3 border border-gray-300 rounded-md focus:ring-[#D4A017] focus:border-[#D4A017] outline-none" placeholder="123" data-id="element-869" />
                        </div>
                      </div>
                      <div className="mb-6" data-id="element-870">
                        <label htmlFor="nameOnCard" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-871">
                          Name on Card
                        </label>
                        <div className="relative" data-id="element-872">
                          <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-873" />
                          <input type="text" id="nameOnCard" name="nameOnCard" value={formData.nameOnCard} onChange={handleChange} required className="pl-10 w-full p-3 border border-gray-300 rounded-md focus:ring-[#D4A017] focus:border-[#D4A017] outline-none" placeholder="John Doe" data-id="element-874" />
                        </div>
                      </div>
                      <div className="mb-6" data-id="element-875">
                        <label className="flex items-center" data-id="element-876">
                          <input type="checkbox" name="agreeToTerms" checked={formData.agreeToTerms} onChange={handleChange} required className="rounded text-[#D4A017] focus:ring-[#D4A017] mr-2" data-id="element-877" />
                          <span className="text-sm text-gray-700" data-id="element-878">
                            I agree to the{' '}
                            <a href="#" className="text-[#006D77] hover:underline" data-id="element-879">
                              Terms and Conditions
                            </a>{' '}
                            and{' '}
                            <a href="#" className="text-[#006D77] hover:underline" data-id="element-880">
                              Privacy Policy
                            </a>
                          </span>
                        </label>
                      </div>
                      <button type="submit" disabled={isSubmitting} className={`w-full bg-[#FF6B6B] hover:bg-[#ff5252] text-white py-3 rounded-md font-medium ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-881">
                        {isSubmitting ? <span className="flex items-center justify-center" data-id="element-882">
                            <span className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2" data-id="element-883"></span>
                            Processing...
                          </span> : 'Complete Booking'}
                      </button>
                    </form>
                  </div>
                </div>
                {/* Booking Summary */}
                <div className="md:col-span-1" data-id="element-884">
                  <div className="bg-white rounded-lg shadow-md p-6 sticky top-24" data-id="element-885">
                    <h2 className="text-xl font-semibold mb-4" data-id="element-886">
                      Booking Summary
                    </h2>
                    <div className="flex items-center mb-4" data-id="element-887">
                      <img src={bookingDetails.propertyImage} alt={bookingDetails.propertyTitle} className="w-20 h-20 object-cover rounded-md mr-4" data-id="element-888" />
                      <div data-id="element-889">
                        <h3 className="font-medium" data-id="element-890">
                          {bookingDetails.propertyTitle}
                        </h3>
                      </div>
                    </div>
                    <div className="border-t border-gray-200 pt-4 mb-4" data-id="element-891">
                      <div className="flex justify-between mb-2" data-id="element-892">
                        <span className="text-gray-700" data-id="element-893">Dates</span>
                        <span className="text-gray-700" data-id="element-894">
                          {formatDate(bookingDetails.checkInDate)} -{' '}
                          {formatDate(bookingDetails.checkOutDate)}
                        </span>
                      </div>
                      <div className="flex justify-between mb-2" data-id="element-895">
                        <span className="text-gray-700" data-id="element-896">Guests</span>
                        <span className="text-gray-700" data-id="element-897">
                          {bookingDetails.guests}
                        </span>
                      </div>
                    </div>
                    <div className="border-t border-gray-200 pt-4 mb-4" data-id="element-898">
                      <div className="flex justify-between mb-2" data-id="element-899">
                        <span className="text-gray-700" data-id="element-900">
                          ${bookingDetails.pricePerNight} x{' '}
                          {bookingDetails.nights} nights
                        </span>
                        <span className="text-gray-700" data-id="element-901">
                          $
                          {bookingDetails.pricePerNight * bookingDetails.nights}
                        </span>
                      </div>
                      <div className="flex justify-between mb-2" data-id="element-902">
                        <span className="text-gray-700" data-id="element-903">Cleaning fee</span>
                        <span className="text-gray-700" data-id="element-904">$60</span>
                      </div>
                      <div className="flex justify-between mb-2" data-id="element-905">
                        <span className="text-gray-700" data-id="element-906">Service fee</span>
                        <span className="text-gray-700" data-id="element-907">$45</span>
                      </div>
                    </div>
                    <div className="border-t border-gray-200 pt-4" data-id="element-908">
                      <div className="flex justify-between font-semibold" data-id="element-909">
                        <span data-id="element-910">Total</span>
                        <span data-id="element-911">${bookingDetails.totalPrice + 60 + 45}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>}
        </div>
      </div>
    </div>;
};
export default BookingPage;